import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => {
    const path = location?.pathname;
    if (['/public-claim-search', '/claim-detail-analysis', '/narrative-explorer']?.includes(path)) {
      return 'verify';
    } else if (['/expert-console-dashboard', '/fact-check-workbench', '/crisis-response-center']?.includes(path)) {
      return 'expert';
    } else if (['/platform-bridge-interface', '/research-data-portal']?.includes(path)) {
      return 'institutional';
    }
    return 'verify';
  });

  const navigationSections = [
    {
      id: 'verify',
      label: 'Verify Claims',
      icon: 'Search',
      description: 'Public verification tools',
      routes: [
        { 
          path: '/public-claim-search', 
          label: 'Claim Search', 
          icon: 'Search',
          description: 'Search and verify claims'
        },
        { 
          path: '/claim-detail-analysis', 
          label: 'Detail Analysis', 
          icon: 'FileText',
          description: 'Detailed claim analysis'
        },
        { 
          path: '/narrative-explorer', 
          label: 'Narrative Explorer', 
          icon: 'GitBranch',
          description: 'Explore narrative patterns'
        }
      ]
    },
    {
      id: 'expert',
      label: 'Expert Console',
      icon: 'Users',
      description: 'Professional workspace',
      routes: [
        { 
          path: '/expert-console-dashboard', 
          label: 'Dashboard', 
          icon: 'BarChart3',
          description: 'Expert overview dashboard'
        },
        { 
          path: '/fact-check-workbench', 
          label: 'Workbench', 
          icon: 'Wrench',
          description: 'Fact-checking workspace'
        },
        { 
          path: '/crisis-response-center', 
          label: 'Crisis Response', 
          icon: 'AlertTriangle',
          description: 'Emergency response center'
        }
      ]
    },
    {
      id: 'institutional',
      label: 'Institutional Access',
      icon: 'Building',
      description: 'Research and management',
      routes: [
        { 
          path: '/platform-bridge-interface', 
          label: 'Platform Bridge', 
          icon: 'Link',
          description: 'Platform integration'
        },
        { 
          path: '/research-data-portal', 
          label: 'Research Portal', 
          icon: 'Database',
          description: 'Research data access'
        }
      ]
    }
  ];

  const handleSectionClick = (sectionId) => {
    if (activeSection === sectionId) {
      return;
    }
    setActiveSection(sectionId);
    const section = navigationSections?.find(s => s?.id === sectionId);
    if (section && section?.routes?.length > 0) {
      window.location.href = section?.routes?.[0]?.path;
    }
  };

  const handleRouteClick = (path) => {
    window.location.href = path;
  };

  const currentRoute = location?.pathname;
  const activeRoutes = navigationSections?.find(s => s?.id === activeSection)?.routes || [];

  return (
    <aside className={`
      fixed left-0 top-16 bottom-0 z-40 bg-card border-r border-border shadow-soft
      transition-all duration-300 ease-in-out
      ${isCollapsed ? 'w-16' : 'w-64'}
      lg:translate-x-0
    `}>
      <div className="flex flex-col h-full">
        {/* Collapse Toggle */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <h2 className="text-sm font-semibold text-foreground">Navigation</h2>
          )}
          <Button
            variant="ghost"
            size="icon"
            iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
            onClick={onToggleCollapse}
            className="ml-auto"
          />
        </div>

        {/* Navigation Sections */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-2 px-3">
            {navigationSections?.map((section) => (
              <div key={section?.id} className="space-y-1">
                {/* Section Header */}
                <button
                  onClick={() => handleSectionClick(section?.id)}
                  className={`
                    w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium
                    transition-smooth group
                    ${activeSection === section?.id
                      ? 'bg-primary text-primary-foreground shadow-soft'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon 
                    name={section?.icon} 
                    size={18} 
                    className={`
                      ${isCollapsed ? 'mx-auto' : 'mr-3'}
                      ${activeSection === section?.id ? 'text-primary-foreground' : ''}
                    `}
                  />
                  {!isCollapsed && (
                    <div className="flex-1 text-left">
                      <div className="font-medium">{section?.label}</div>
                      <div className="text-xs opacity-75">{section?.description}</div>
                    </div>
                  )}
                </button>

                {/* Section Routes */}
                {!isCollapsed && activeSection === section?.id && (
                  <div className="ml-6 space-y-1 border-l border-border pl-4">
                    {section?.routes?.map((route) => (
                      <button
                        key={route?.path}
                        onClick={() => handleRouteClick(route?.path)}
                        className={`
                          w-full flex items-center px-3 py-2 rounded-md text-sm
                          transition-smooth group
                          ${currentRoute === route?.path
                            ? 'bg-accent/10 text-accent font-medium' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                          }
                        `}
                      >
                        <Icon 
                          name={route?.icon} 
                          size={16} 
                          className="mr-3"
                        />
                        <div className="flex-1 text-left">
                          <div className="font-medium">{route?.label}</div>
                          <div className="text-xs opacity-75">{route?.description}</div>
                        </div>
                        {currentRoute === route?.path && (
                          <div className="w-2 h-2 bg-accent rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Contextual Action Bar */}
        <div className="border-t border-border p-4">
          {!isCollapsed ? (
            <div className="space-y-3">
              {/* Real-time Collaboration Status */}
              <div className="flex items-center space-x-2 px-3 py-2 bg-muted/50 rounded-lg">
                <div className="flex -space-x-1">
                  <div className="w-6 h-6 bg-success rounded-full border-2 border-card flex items-center justify-center">
                    <Icon name="User" size={12} color="white" />
                  </div>
                  <div className="w-6 h-6 bg-warning rounded-full border-2 border-card flex items-center justify-center">
                    <Icon name="User" size={12} color="white" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-xs font-medium text-foreground">2 Active Users</div>
                  <div className="text-xs text-muted-foreground">Collaborative session</div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    // Quick action functionality
                  }}
                >
                  New Analysis
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Bell"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    // Notifications functionality
                  }}
                >
                  Alerts (3)
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="icon"
                iconName="Plus"
                onClick={() => {
                  // Quick action functionality
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                iconName="Bell"
                onClick={() => {
                  // Notifications functionality
                }}
              />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;